/* ButterCo cart drawer: renders the side cart and the cart page (every [data-bc-cview]) and handles their buttons,
   the product cards' Add and +/−, and the black-rice form's Add. window.BCCart is the API the product page and the
   BYOB builder call.
   Loaded as an async module so it is ready before the cart icon shows (docs/steps/step-05.md). It can therefore run
   before the header is parsed or after the whole page is: until it is needed it only listens on document, and it
   finds the drawer and reads #bc-cart-data (snippets/bc-cart-drawer.liquid) when it first renders or acts. A tap
   that comes before that falls back to the plain link or form (the /cart page).
   Cart reads and changes go through fetch, so bc-core.js shares, tracks and hooks them like any other.
   Globals: BCCart, BC_MRP / BC_PRICE (compare-at and price by variant id; bc-gifts.js reads BC_PRICE). */
if (!window.BCCart) {
  const MRP = (window.BC_MRP ||= {});
  const PRICE = (window.BC_PRICE ||= {});
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  let cfg = null;
  /** #bc-cart-data once the parser has got past it, else null. */
  function config() {
    if (cfg) return cfg;
    try {
      cfg = JSON.parse(document.getElementById('bc-cart-data').textContent);
    } catch {
      return null;
    }
    for (const [id, price, was] of cfg.variants) {
      PRICE[id] = price;
      if (was > price) MRP[id] = was;
    }
    Object.assign(MRP, cfg.cartMrp);
    return cfg;
  }
  const parsed = new Promise((resolve) => {
    if (document.readyState === 'loading')
      document.addEventListener('DOMContentLoaded', resolve, { once: true });
    else resolve();
  });
  const route = (name) => config().routes[name];

  const money = (paise) => {
    const n = paise / 100;
    return cfg.zero.replace('0', n % 1 ? n.toFixed(2) : n.toLocaleString('en-IN'));
  };
  const esc = (s) =>
    String(s ?? '').replace(
      /[&<>"]/g,
      (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch],
    );
  const img = (url, width) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : '');
  const short = (title) =>
    String(title || '')
      .split(' | ')[0]
      .split(' - ')[0];
  const errText = (e, fallback) => (e && (e.description || e.message)) || fallback;
  const onCartPage = () => document.body.classList.contains('template-cart');
  const idle = () => (window.BCF?.idle ? window.BCF.idle() : Promise.resolve());

  async function jf(url, body) {
    const r = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: JSON_HEADERS,
      body: body ? JSON.stringify(body) : undefined,
    });
    const d = await r.json();
    if (!r.ok) throw d;
    return d;
  }

  // hold: the drawer was opened busy for an add that hasn't finished. settling: gifts are being checked again
  // after a bundle price (bc:price-final).
  const state = { cart: null, busy: false, hold: false, err: '', settling: 0 };
  const pending = () => !!window.BCPrice?.pending || state.settling > 0;

  function wave(p) {
    const pct = Math.max(0, Math.min(100, p));
    const d = 'M0 6Q12.5 0 25 6T50 6T75 6T100 6T125 6T150 6T175 6T200 6T225 6T250 6T275 6T300 6';
    return `<div class="bc-ship__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}"><svg viewBox="0 0 300 12" preserveAspectRatio="none" aria-hidden="true"><path class="bc-ship__track" d="${d}"/><path class="bc-ship__fill" style="clip-path:inset(0 ${100 - pct}% 0 0)" d="${d}"/></svg></div>`;
  }
  function shipHtml(c) {
    const left = cfg.freeShip - c.total_price;
    const msg =
      left > 0
        ? `Add <b>${money(left)}</b> more for free shipping!`
        : "🎉 You've unlocked <b>free shipping</b>!";
    return `<div class="bc-ship"><p>${msg}</p>${wave((c.total_price / cfg.freeShip) * 100)}</div>`;
  }

  const isFree = (i) => !!i.properties?._freebie;
  const mrpOf = (i) => Math.max(MRP[i.variant_id] || 0, i.original_price || 0, i.final_price || 0);
  const asked = {};
  /** Compare-at prices the page didn't come with (a product added after load): one request per product. */
  function fillMrp(c) {
    for (const i of c.items || []) {
      if (i.variant_id in MRP || !i.handle || asked[i.handle]) continue;
      asked[i.handle] = 1;
      fetch(`/products/${i.handle}.js`)
        .then((r) => r.json())
        .then((p) => {
          for (const v of p.variants || []) MRP[v.id] = v.compare_at_price || 0;
          MRP[i.variant_id] = MRP[i.variant_id] || 0;
          render();
        })
        .catch(() => {
          MRP[i.variant_id] = 0;
        });
    }
  }

  /* Free gift moment: gold highlight when a gift is unlocked, a gift ladder (spend amounts only) and the next
     locked gift (its worth only, never its name). */
  const giftConfig = () => window.BC_FREEBIES || { rules: [], all: [] };
  let giftsBefore = null;
  let newGift = null;
  let settleTimer = 0;
  const paidQty = (c) => (c.items || []).reduce((n, i) => (isFree(i) ? n : n + i.quantity), 0);
  function paidAmount(c) {
    const lines = (c.items || []).reduce((n, i) => (isFree(i) ? n : n + (i.final_line_price || 0)), 0);
    const cartDiscount = Math.max(0, (c.items_subtotal_price || 0) - (c.total_price || 0));
    return Math.max(0, lines - cartDiscount);
  }
  function giftsIn(c) {
    const have = {};
    for (const i of c.items || []) if (isFree(i)) have[i.variant_id] = 1;
    return have;
  }
  const giftSteps = () =>
    (giftConfig().rules || [])
      .slice()
      .sort((a, b) => a.amt - b.amt)
      .map((r) => ({ amt: r.amt, id: r.ids[r.ids.length - 1] }));
  function nextGift(c) {
    const paid = paidAmount(c);
    const have = giftsIn(c);
    for (const step of giftSteps())
      if (!have[step.id]) return { id: step.id, need: Math.max(0, step.amt - paid) };
    return null;
  }
  function trackGifts(c) {
    const have = giftsIn(c);
    const now = Date.now();
    if (giftsBefore) {
      const added = Object.keys(have).filter((id) => !giftsBefore[id]);
      const gone = Object.keys(giftsBefore).filter((id) => !have[id]);
      if (added.length) {
        newGift = { id: +added[added.length - 1], until: now + 3200, shown: false };
        setTimeout(() => {
          if (newGift) newGift.shown = true;
        }, 700);
      } else if (gone.length) newGift = null;
    }
    giftsBefore = have;
    if (newGift && now > newGift.until) newGift = null;
    clearTimeout(settleTimer);
    if (newGift) settleTimer = setTimeout(settleGift, newGift.until - now + 30);
  }
  /** Ends the gold moment in place, without a re-render, so the transition plays. */
  function settleGift() {
    if (!newGift || Date.now() < newGift.until - 60) return;
    newGift = null;
    for (const li of document.querySelectorAll('.bc-ci--new')) {
      li.classList.add('is-settled');
      const cap = li.querySelector('.bc-ci__free--new');
      if (cap) {
        cap.classList.add('is-settled');
        setTimeout(() => {
          cap.textContent = 'Free gift';
        }, 300);
      }
    }
    for (const node of document.querySelectorAll('.bc-gl__node.is-new')) {
      node.classList.add('is-settled');
      setTimeout(() => {
        node.innerHTML = G_TICK;
      }, 300);
    }
    for (const el of document.querySelectorAll('.bc-gl__labels .is-new,.bc-csum__neg.is-gold')) {
      el.classList.add('is-settled');
    }
  }
  const isNewGift = (i) => !!(newGift && isFree(i) && i.variant_id === newGift.id);
  const G_ICON =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="13" rx="2"></rect><path d="M12 8v13M3 12h18M12 8c-2-4-6-4-6-1s4 1 6 1zm0 0c2-4 6-4 6-1s-4 1-6 1z"></path></svg>';
  const G_LOCK =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"></rect><path d="M8 11V8a4 4 0 0 1 8 0v3"></path></svg>';
  const G_TICK =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7"></path></svg>';
  const giftAmount = (a) => (a <= 100 ? 'Any order' : money(a));

  function ladderHtml(c) {
    const steps = giftSteps();
    if (steps.length < 2 || paidQty(c) < 1) return '';
    const have = giftsIn(c);
    const paid = paidAmount(c);
    const done = steps.filter((s) => have[s.id]).length;
    // The fill follows the amount paid between steps, so shoppers see how close the next gift is.
    let pct = 100;
    for (let k = 0; k < steps.length - 1; k++) {
      if (paid < steps[k + 1].amt) {
        const f = Math.max(0, Math.min(1, (paid - steps[k].amt) / (steps[k + 1].amt - steps[k].amt)));
        pct = ((k + f) / (steps.length - 1)) * 100;
        break;
      }
    }
    if (paid < steps[0].amt) pct = 0;
    pct = Math.round(pct * 10) / 10;
    const next = nextGift(c);
    const worth = next ? MRP[next.id] || 0 : 0;
    let head;
    if (!next) head = `<b>All ${steps.length} gifts unlocked</b>`;
    else if (next.need > 0) {
      head = `<b>${money(next.need)} away from ${worth ? `a gift worth ${money(worth)}` : 'your next gift'}</b>`;
    } else head = '<b>Unlocking your next gift…</b>';
    const isNew = (s) => !!newGift && newGift.id === s.id;
    const nodes = steps
      .map((s, k) => {
        const cls =
          'bc-gl__node' +
          (have[s.id] ? ' is-on' : '') +
          (isNew(s) ? ' is-new' + (newGift.shown ? ' is-shown' : '') : '');
        const icon = isNew(s) ? G_ICON : have[s.id] ? G_TICK : G_LOCK;
        return `<span class="${cls}" style="left:${(k / (steps.length - 1)) * 100}%">${icon}</span>`;
      })
      .join('');
    const labels = steps
      .map((s) => {
        const w = MRP[s.id] || 0;
        const cls = (have[s.id] ? 'is-on' : '') + (isNew(s) ? ' is-new' : '');
        return `<span class="${cls}"><b>${giftAmount(s.amt)}</b>${w ? `<small>${money(w)} gift</small>` : ''}</span>`;
      })
      .join('');
    return `<div class="bc-gl"><div class="bc-gl__t">${head}<span class="bc-gl__n">${done}/${steps.length}</span></div><div class="bc-gl__bar"><span class="bc-gl__track"></span><span class="bc-gl__fill" style="width:${pct}%"></span>${nodes}</div><div class="bc-gl__labels">${labels}</div></div>`;
  }

  function itemHtml(i) {
    const free = isFree(i);
    const fresh = isNewGift(i);
    const mrp = mrpOf(i);
    const cls = 'bc-ci' + (fresh ? ' bc-ci--new' + (newGift.shown ? ' is-shown' : '') : '');
    const variant =
      i.variant_title && i.variant_title !== 'Default Title' ? `<small>${esc(i.variant_title)}</small>` : '';
    const pic = i.image ? `<img src="${esc(img(i.image, 200))}" alt="" loading="lazy">` : '';
    const each =
      (mrp > i.final_price ? `<s>${money(mrp)}</s> ` : '') + (free ? 'Free' : `${money(i.final_price)} each`);
    let right;
    if (free) {
      right = fresh
        ? '<span class="bc-ci__free bc-ci__free--new">New gift</span>'
        : '<span class="bc-ci__free">Free gift</span>';
    } else {
      right = i.properties?._byob_mini
        ? ''
        : `<div class="bc-mini-pill"><button type="button" data-cq="-1" aria-label="Remove one">−</button><b>${i.quantity}</b><button type="button" data-cq="1" aria-label="Add one">+</button></div>`;
      right +=
        (mrp * i.quantity > i.final_line_price
          ? `<s class="bc-ci__mrp">${money(mrp * i.quantity)}</s>`
          : '') + `<span class="bc-ci__line">${money(i.final_line_price)}</span>`;
    }
    return `<li class="${cls}" data-key="${esc(i.key)}"><a href="${esc(i.url)}" class="bc-ci__img">${pic}</a><div class="bc-ci__info"><a href="${esc(i.url)}" class="bc-ci__t">${esc(short(i.product_title))}</a>${variant}<span class="bc-ci__each">${each}</span></div><div class="bc-ci__r">${right}</div></li>`;
  }

  /** Bundle a line belongs to: Rich Bundle Builder instance, BYOB bundle, or Easy Bundle offer + box. */
  function bundleOf(i) {
    const p = i.properties || {};
    if (p._rlb_bundle_instance_id) return p._rlb_bundle_instance_id;
    if (p._byob_bundle) return p._byob_bundle;
    if (p._bundleOfferId) return String(p._bundleOfferId).split('_').slice(0, 2).join('_');
    return null;
  }
  /** Lines in cart order, each bundle as one group where its first line is. */
  function listHtml(items) {
    const groups = {};
    const out = [];
    for (const i of items) {
      const id = bundleOf(i);
      if (!id) out.push({ line: i });
      else if (groups[id]) groups[id].push(i);
      else {
        groups[id] = [i];
        out.push({ bundle: id });
      }
    }
    return out.map((o) => (o.line ? itemHtml(o.line) : bundleHtml(o.bundle, groups[o.bundle]))).join('');
  }
  function bundleHtml(id, lines) {
    const first = lines[0].properties;
    let name =
      first._rlb_bundle_name ||
      first._byob_name ||
      (first._bundleOfferId ? 'Build Your Own Bundle' : 'Your bundle');
    if (/mix\s*(&|and)?\s*match/i.test(name) || /^\s*byob\s*$/i.test(name)) name = 'Build Your Own Bundle';
    let price = 0;
    let was = 0;
    let full = 0;
    for (const i of lines) {
      price += i.final_line_price;
      was += Math.max(i.original_line_price, mrpOf(i) * i.quantity);
      full += i.original_line_price;
    }
    // One code covers every bundle and Shopify spreads it over all of them, so each bundle shows its own tier
    // price (docs/constraints.md #11). Minis are not part of the tier and keep their own price (#10).
    const products = lines.filter((i) => !i.properties._byob_mini);
    const count = products.reduce((n, i) => n + i.quantity, 0);
    const tier = products.some((i) => i.properties._bundleOfferId) && window.BC_BYOB?.tiers[count];
    if (tier && price < full) {
      price = tier * 100 + lines.reduce((n, i) => (i.properties._byob_mini ? n + i.final_line_price : n), 0);
    }
    const foot = pending()
      ? '<span class="bc-skel bc-skel--s" aria-hidden="true"></span><span class="bc-skel" role="status" aria-label="Applying bundle price"></span>'
      : (was > price ? `<s>${money(was)}</s>` : '') + `<b>${money(price)}</b>`;
    const list = lines
      .map((i) => {
        const pic = i.image ? `<img src="${esc(img(i.image, 160))}" alt="" loading="lazy">` : '';
        const qty = i.quantity > 1 ? ` <small>×${i.quantity}</small>` : '';
        return `<li><a href="${esc(i.url)}" class="bc-ci__img bc-ci__img--s">${pic}</a><span class="bc-bun__n">${esc(short(i.product_title))}${qty}</span></li>`;
      })
      .join('');
    return `<li class="bc-bun" data-bun="${esc(id)}"><div class="bc-bun__head"><span class="bc-bun__tag">Bundle</span><b>${esc(name)}</b><button type="button" class="bc-bun__rm" data-bc-rmb="${esc(id)}">Remove</button></div><ul class="bc-bun__list">${list}</ul><div class="bc-bun__foot">${foot}</div></li>`;
  }

  const emptyHtml = () =>
    `<div class="bc-cempty"><div class="bc-cempty__ic" aria-hidden="true"><svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8h6l5 22h22l4-15H14"/><circle cx="20" cy="38" r="3"/><circle cx="36" cy="38" r="3"/></svg></div><h3>Your cart is empty</h3><p>Add some curl care goodness!</p><a class="bc-btn" href="${cfg.routes.all}">Shop Now →</a></div>`;

  const written = new WeakMap();
  /** Identical HTML is not written again: that would reload images, restart animations and drop focus. */
  function write(view, html) {
    if (written.get(view) === html) return;
    written.set(view, html);
    view.innerHTML = html;
  }

  function render() {
    const c = state.cart;
    if (!c || !config()) return;
    fillMrp(c);
    if (c.item_count) trackGifts(c);
    const isPending = pending();
    for (const view of document.querySelectorAll('[data-bc-cview]')) {
      const inDrawer = view.dataset.bcCview === 'drawer';
      const head = `<div class="bc-ch"><h2>Your Cart (${c.item_count})</h2>${inDrawer ? '<button type="button" class="bc-ch__x" data-bc-cclose aria-label="Close cart">×</button>' : ''}</div>`;
      view.classList.toggle('is-busy', state.busy);
      if (state.busy) view.setAttribute('aria-busy', 'true');
      else view.removeAttribute('aria-busy');
      if (!c.item_count) {
        write(view, head + shipHtml(c) + emptyHtml());
        continue;
      }
      const err = state.err ? `<p class="bc-cerr">⚠ ${esc(state.err)}</p>` : '';
      const giftOrder = window.BC_FREEBIES?.all || [];
      const gifts = c.items
        .filter(isFree)
        .sort((x, y) => giftOrder.indexOf(x.variant_id) - giftOrder.indexOf(y.variant_id));
      const items =
        ladderHtml(c) +
        `<ul class="bc-clist">${listHtml(gifts)}${listHtml(c.items.filter((i) => !isFree(i)))}</ul>`;
      const subtotal = isPending
        ? '<span class="bc-skel bc-skel--sub" role="status" aria-label="Updating price"></span>'
        : `<b>${money(c.total_price)}</b>`;
      const more = inDrawer
        ? '<button type="button" class="bc-csum__cont" data-bc-cclose>Continue shopping</button>'
        : `<a class="bc-csum__cont" href="${cfg.routes.all}">Continue shopping</a>`;
      const sum = `<div class="bc-csum"><div class="bc-csum__row"><span>Subtotal</span>${subtotal}</div><p class="bc-csum__note">Taxes and shipping calculated at checkout.</p><button type="button" class="sr-headless-checkout bc-btn bc-csum__go" style="border-radius:999px!important;height:auto!important;min-height:54px!important;font-family:DM Sans,sans-serif!important;font-weight:700!important;font-size:16px!important"${isPending ? ' disabled aria-busy="true"' : ''} onclick="shiprocketCheckoutEvents.buyCart(event)">Checkout →</button>${more}</div>`;
      write(
        view,
        inDrawer
          ? `${head}${shipHtml(c)}<div class="bc-cbody">${err}${items}</div>${sum}`
          : `${head}${shipHtml(c)}<div class="bc-cpage__grid"><div>${err}${items}</div>${sum}</div>`,
      );
      // Set on the live buttons rather than in the HTML, so a busy state doesn't re-create the whole view. Not
      // `disabled`: that would drop keyboard focus out of the dialog; taps while busy are ignored anyway.
      for (const b of view.querySelectorAll('[data-cq],[data-bc-rmb]')) {
        if (state.busy) b.setAttribute('aria-disabled', 'true');
        else b.removeAttribute('aria-disabled');
      }
    }
    window.bcSetCount?.(c.item_count);
    syncCards(c);
  }

  /** Quantity and first line key per variant, outside bundles. */
  function linesByVariant(c) {
    const m = {};
    for (const i of c.items || []) {
      if (bundleOf(i)) continue;
      const v = (m[i.variant_id] ||= { q: 0, key: null });
      v.q += i.quantity;
      if (!v.key) v.key = i.key;
    }
    return m;
  }
  /** Product cards show Add or their +/− pill for what is in the cart; bc-product.liquid listens to bc:cart. */
  function syncCards(c) {
    const m = linesByVariant(c);
    for (const w of document.querySelectorAll('[data-bc-card-atc]')) {
      if (w.dataset.bcPending) continue;
      const q = m[w.dataset.variant]?.q || 0;
      const add = w.querySelector('[data-bc-cadd]');
      const pill = w.querySelector('.bc-cpill');
      const count = w.querySelector('[data-bc-cn]');
      if (add) {
        add.hidden = q > 0;
        if (add.dataset.t) {
          add.textContent = add.dataset.t;
          delete add.dataset.t;
          add.disabled = false;
        }
        if (add.hasAttribute('data-bc-so')) add.disabled = true;
      }
      if (pill) pill.hidden = q < 1;
      if (count) count.textContent = q;
      w.classList.remove('is-busy');
    }
    document.dispatchEvent(new CustomEvent('bc:cart', { detail: c }));
  }

  /** The cart, once #bc-cart-data is there and gifts are not being changed. */
  async function read() {
    if (!config()) await parsed;
    await idle();
    return jf(`${route('cart')}.js`);
  }
  async function refresh() {
    const c = await read();
    state.cart = c;
    state.busy = state.hold;
    render();
  }
  function change(key, quantity) {
    state.busy = true;
    state.err = '';
    render();
    return jf(`${route('change')}.js`, { id: key, quantity })
      .then((c) => {
        state.cart = c;
        state.busy = false;
        render();
      })
      .catch((e) => {
        state.busy = false;
        state.err = errText(e, 'Could not update the cart.');
        refresh();
      });
  }
  function add(id, quantity) {
    state.err = '';
    state.busy = true;
    if (state.cart) render();
    return jf(`${route('add')}.js`, { items: [{ id: +id, quantity: quantity || 1 }] })
      .then(() => refresh())
      .catch((e) => {
        state.err = errText(e, 'Could not add to cart.');
        return refresh();
      });
  }
  /** Removes every line of a bundle, and the Easy Bundle codes that no longer apply without it. */
  function removeBundle(id) {
    const updates = {};
    for (const i of state.cart.items) if (bundleOf(i) === id) updates[i.key] = 0;
    state.busy = true;
    render();
    jf(`${route('cart')}/update.js`, { updates })
      .then((c) => {
        const codes = c?.discount_codes || [];
        const keep = codes
          .filter((d) => !(String(d.code).startsWith('EasyBundle-') && !d.applicable))
          .map((d) => d.code);
        return keep.length < codes.length
          ? jf(`${route('cart')}/update.js`, { discount: keep.join(',') })
          : c;
      })
      .then((c) => {
        state.cart = c;
        state.busy = false;
        render();
      })
      .catch(() => {
        state.busy = false;
        refresh();
      });
  }

  let lastFocus = null;
  let closeTimer = 0;
  /** The drawer, moved to <body> so no header stacking or transform can clip the fixed panel. */
  function drawer() {
    const dr = document.getElementById('bc-cart-drawer');
    if (dr && dr.parentNode !== document.body) document.body.appendChild(dr);
    return dr;
  }
  function open() {
    const dr = drawer();
    if (!dr) return;
    lastFocus = document.activeElement;
    clearTimeout(closeTimer);
    dr.hidden = false;
    void dr.offsetWidth; // start the slide-in from the closed position
    dr.classList.add('is-open');
    dr.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bc-lock');
    // Only if still open: a drawer closed before its cart came must not pull focus back into it.
    refresh().then(() => {
      if (dr.classList.contains('is-open')) dr.querySelector('.bc-ch__x')?.focus();
    });
  }
  function close() {
    const dr = drawer();
    if (!dr || dr.hidden) return;
    dr.classList.remove('is-open');
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      if (!dr.classList.contains('is-open')) dr.hidden = true;
    }, 340);
    dr.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('bc-lock');
    lastFocus?.focus?.();
  }
  function openBusy() {
    if (onCartPage()) return;
    state.hold = true;
    state.busy = true;
    if (state.cart) render();
    open();
  }
  function done() {
    state.hold = false;
    return refresh();
  }

  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-bc-card-atc] [data-bc-cd]');
    if (step) {
      if (!config()) return;
      e.preventDefault();
      cardStep(step);
      return;
    }
    const t = e.target.closest('[data-bc-cclose],[data-cq],[data-bc-open-cart],[data-bc-rmb]');
    if (!t) return;
    if (t.hasAttribute('data-bc-open-cart')) {
      if (onCartPage() || !config()) return;
      e.preventDefault();
      open();
      return;
    }
    if (t.hasAttribute('data-bc-cclose')) {
      e.preventDefault();
      close();
      return;
    }
    if (state.busy) return;
    if (t.hasAttribute('data-cq')) {
      const key = t.closest('.bc-ci').dataset.key;
      const line = state.cart.items.find((i) => i.key === key);
      change(line.key, Math.max(0, line.quantity + +t.dataset.cq));
      return;
    }
    removeBundle(t.getAttribute('data-bc-rmb'));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  /** A product card's +/−: the drawer opens busy for a +, the card is busy until the cart is back. */
  function cardStep(btn) {
    const w = btn.closest('[data-bc-card-atc]');
    if (!w || w.classList.contains('is-busy')) return;
    const d = +btn.dataset.bcCd;
    w.classList.add('is-busy');
    const go = () => {
      const line = linesByVariant(state.cart)[w.dataset.variant];
      if (d > 0 && !line) return add(w.dataset.variant, 1);
      if (!line) return refresh();
      return change(line.key, Math.max(0, line.q + d));
    };
    w.dataset.bcPending = '1';
    // Owner (30 Oct): only a card's first Add opens the drawer; its + and - then update the cart quietly.
    const finish = () => {
      delete w.dataset.bcPending;
      if (state.hold) {
        state.hold = false;
        state.busy = false;
        render();
      }
      w.classList.remove('is-busy');
      if (state.cart) syncCards(state.cart);
    };
    (state.cart ? Promise.resolve() : refresh()).then(go).then(() => {
      finish();
      if (state.err) open();
      else if (d > 0) bump();
    }, finish);
  }

  /* Black Rice page (and any .br-pd__form): add without leaving the page, the drawer open and busy until done.
     Capture phase, so it runs before the form's own handlers. */
  document.addEventListener(
    'submit',
    (e) => {
      const f = e.target;
      if (!f?.matches?.('.br-pd__form') || !config()) return;
      e.preventDefault();
      e.stopPropagation();
      const b = f.querySelector('[type=submit]');
      if (b?.classList.contains('bc-loading')) return;
      if (b) {
        b.dataset.t = b.innerHTML;
        b.textContent = 'Adding…';
        b.classList.add('bc-loading');
      }
      openBusy();
      fetch(`${route('add')}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(f),
      })
        .then((r) =>
          r.json().then((d) => {
            if (!r.ok) throw d;
            return d;
          }),
        )
        .catch((er) => {
          state.err = errText(er, 'Could not add to cart.');
        })
        .then(done)
        .then(() => {
          if (b) {
            b.innerHTML = b.dataset.t;
            b.classList.remove('bc-loading');
          }
        });
    },
    true,
  );

  function bump() {
    const icon = document.querySelector('.bc-icons__cart');
    if (!icon) return;
    icon.classList.remove('bc-bump');
    void icon.offsetWidth; // restart the animation
    icon.classList.add('bc-bump');
  }
  /** A product card's Add: the drawer opens busy, the button says "Adding…" until the cart is back. */
  function cardAdd(w, b, id) {
    if (w) w.dataset.bcPending = '1';
    if (!onCartPage()) {
      state.hold = true;
      open();
    }
    if (b) {
      b.disabled = true;
      b.dataset.t = b.textContent;
      b.textContent = 'Adding…';
    }
    w?.classList.add('is-busy');
    return add(id, 1).then(() => {
      if (b?.dataset.t) {
        b.disabled = false;
        b.textContent = b.dataset.t;
        delete b.dataset.t;
      }
      w?.classList.remove('is-busy');
      if (w) {
        delete w.dataset.bcPending;
        if (state.cart) syncCards(state.cart);
      }
      if (state.hold) {
        state.hold = false;
        state.busy = false;
        render();
      }
      if (state.err) open();
      else bump();
    });
  }
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!f.closest || !(f.closest('.bc-card') || f.hasAttribute('data-bc-card-atc'))) return;
    const id = f.querySelector('[name=id]');
    if (!id || !config()) return;
    e.preventDefault();
    if (f.querySelector('[data-bc-so]')) return;
    cardAdd(
      f.closest('[data-bc-card-atc]'),
      f.querySelector('[data-bc-cadd]') || f.querySelector('button'),
      id.value,
    );
  });
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-bc-card-atc] [data-bc-cadd]');
    if (!b || b.form || !config()) return;
    e.preventDefault();
    const w = b.closest('[data-bc-card-atc]');
    cardAdd(w, b, w.dataset.variant);
  });

  window.BCCart = {
    openBusy,
    done,
    /** Opens the drawer on a fresh cart (not from the shared cache): the BYOB builder, after its add. */
    show() {
      state.hold = false;
      return Promise.resolve()
        .then(() =>
          fetch(`${route('cart')}.js`, {
            cache: 'no-store',
            __bcFresh: 1,
            headers: { Accept: 'application/json' },
          }),
        )
        .then((r) => r.json())
        .then((c) => {
          state.cart = c;
          state.busy = false;
          render();
          open();
        })
        .catch(() => refresh());
    },
    open,
    close,
    refresh,
    add,
    sync() {
      if (state.cart) syncCards(state.cart);
      else refresh();
    },
  };

  /* Bundle price: bc-bundles.js reports when the bundle discount is being applied and when the priced cart is in. */
  document.addEventListener('bc:price-pending', () => {
    if (state.cart) render();
  });
  document.addEventListener('bc:price-final', (e) => {
    const priced = e.detail?.items ? e.detail : null;
    if (priced) {
      state.cart = priced;
      state.busy = state.hold;
    }
    // bc-gifts.js checks gifts again on a final price. Keep the skeleton (and Checkout disabled) until it has, so a
    // gift the new price no longer earns is never shown next to it; at most 6 s, the checkout gate's cap (#5), so a
    // stalled gift request can't hold checkout. A later gift change still re-renders (bc:cart-mutated).
    let settled = false;
    const settle = (c) => {
      if (settled) return;
      settled = true;
      clearTimeout(cap);
      if (c) {
        state.cart = c;
        state.busy = state.hold;
      }
      state.settling--;
      render();
    };
    state.settling++;
    const cap = setTimeout(settle, 6000, null);
    render();
    (window.BCF?.ensure ? window.BCF.ensure() : Promise.resolve())
      .then(read)
      .then(settle, () => settle(null));
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refresh();
  });
  /* A cart change sent with fetch (an app, a gift, this drawer) re-renders the views; bc-core.js fires
     bc:cart-mutated when one finishes. */
  let mutated = 0;
  document.addEventListener('bc:cart-mutated', () => {
    clearTimeout(mutated);
    mutated = setTimeout(refresh, 250);
  });

  config();
  parsed.then(() => {
    if (!config()) return;
    drawer();
    if (document.querySelector('[data-bc-cview="page"],[data-bc-card-atc],[data-bc-atc]')) refresh();
  });
}
